import { useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "react-bootstrap";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import SentimentAnalysisTable from "../components/sentimentAnalysisTable";
import { PaginationSentimentAnalysis, SentimentAnalysisApiItem, SentimentAnalysisListFields } from "../types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Search from "../../../../components/Search";
import { useUserPermissions } from "../../../../utils/auth";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../../../constants/permissions";
import { deleteSentiment, getSentiments } from "../api/sentimentAnalysisAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";

// TODO: Refactor. Para añadir un nueva columna a la tabla se debe tocar en demasiados sitios.
export default function SentimentAnalysis() {
    const { t } = useTranslation(["claim", "dashboard", "common"]);
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearchText, setAppliedSearchText] = useState("");
    const [sentiments, setSentiments] = useState<SentimentAnalysisApiItem[]>([]);
    const [sentimentToDelete, setSentimentToDelete] = useState<SentimentAnalysisListFields | null>(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const permissions = useUserPermissions();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const loadSentiments = useCallback(async () => {
        try {
            const response = await getSentiments();
            setSentiments(response ?? []);
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        }
    }, [showAlert, t]);

    useEffect(() => {
        loadSentiments();
    }, [loadSentiments]);

    const sentimentAnalysisData: PaginationSentimentAnalysis = useMemo(() => {
        const items = sentiments.map((item) => {
            const sentimentKey = item.type.toLowerCase() as "negative" | "neutral" | "positive";
            const sentiment =
                sentimentKey === "negative"
                    ? t("claim:newClaimMessageFeelingNegative")
                    : sentimentKey === "positive"
                        ? t("claim:newClaimMessageFeelingPositive")
                        : t("claim:newClaimMessageFeelingNeutral");

            return {
                id: item.id,
                value: item.value,
                sentiment,
                sentimentKey,
                startDate: item.startDate ? new Date(item.startDate) : null,
                // endDate: item.endDate ? new Date(item.endDate) : "", Descomentar cuando se añadan fechas de vigencia
                criteria: item.criteria
            };
        });

        return {
            page: 1,
            size: items.length,
            totalPages: 1,
            totalRecords: items.length,
            items
        };
    }, [sentiments, t]);

    const filteredSentimentAnalysisData: PaginationSentimentAnalysis = useMemo(() => {
        if (!appliedSearchText.trim()) {
            return sentimentAnalysisData;
        }

        const normalizedSearch = appliedSearchText.trim().toLowerCase();
        const filteredItems = sentimentAnalysisData.items.filter((item) => {
            const valueMatch = item.value.toString().includes(normalizedSearch);
            const sentimentMatch = item.sentiment.toLowerCase().includes(normalizedSearch);
            return valueMatch || sentimentMatch;
        });

        return {
            ...sentimentAnalysisData,
            size: filteredItems.length,
            totalRecords: filteredItems.length,
            items: filteredItems
        };
    }, [appliedSearchText, sentimentAnalysisData]);

    const handleSearchInput = () => {
        setAppliedSearchText(searchTerm);
    };

    const handleClear = () => {
        setSearchTerm("");
        setAppliedSearchText("");
    };

    const handleDeleteClick = (item: SentimentAnalysisListFields) => {
        setSentimentToDelete(item);
    };

    const handleEditClick = (id: number) => {
        navigate(`/admin/sentiment-analysis/edit/${id}`);
    }

    const handleCloseDeleteModal = () => {
        if (isDeleteLoading) {
            return;
        }
        setSentimentToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!sentimentToDelete) {
            return;
        }

        setIsDeleteLoading(true);
        try {
            await deleteSentiment(sentimentToDelete.id);
            setSentimentToDelete(null);
            queryClient.invalidateQueries({ queryKey: ['sentiments'] });
            await loadSentiments();
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return (
        <>
            <Modal show={!!sentimentToDelete} onHide={handleCloseDeleteModal}>
                <Modal.Body>
                    {sentimentToDelete &&
                        `Estas segur d'eliminar el valor ${sentimentToDelete.value} de tipus ${sentimentToDelete.sentiment}?`}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleteLoading}>
                        No
                    </Button>
                    <Button variant="primary" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                        Sí
                    </Button>
                </Modal.Footer>
            </Modal>

            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("dashboard:menuGlobalSentimentAnalysis")}</h2>
                </Col>
                {permissions.length > 0 && permissions.includes(PERMISSIONS.SENTIMENT_PARAMETERS.CREATE) && (
                    <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                        <Button
                            variant="primary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => navigate("/admin/sentiment-analysis/new-sentiment")}
                        >
                            {t("common:buttons.buttonNewSentiment")}
                        </Button>
                    </Col>
                )}
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>

            <Row>
                <div style={{ marginBottom: "5px" }}>
                    <Search
                        searchText={searchTerm}
                        onChange={(text) => setSearchTerm(text)}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </div>
            </Row>

            <SentimentAnalysisTable
                sentimentAnalysisData={filteredSentimentAnalysisData}
                totalPages={filteredSentimentAnalysisData.totalPages}
                currentPage={filteredSentimentAnalysisData.page}
                pageSize={filteredSentimentAnalysisData.size}
                sentimentAnalysisActions={{ onDelete: handleDeleteClick, onEdit: handleEditClick }}
            />
        </>
    );
}


